import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TitleBarComponent from "../../../components/titlebar/TitleBarComponent";
import { ISeatDetail, ITripDetail } from "../../../models/trip";
import { getDetailTripApi } from "../../../apis/trip.api";
import LoadingComponent from "../../../components/loading/LoadingComponent";
import { formatLocalDate } from "../../../function/date";

import ButtonAddBus from "./button/ButtonAddBus";
import BusItem from "./components/BusItem";
import GroupButtonActionTrip from "./components/GroupButtonActionTrip";
import { message } from "antd";
import ActionSeatBar from "./components/ActionSeatBar";
import { listStatusTrip } from "../../../constant/listStatusTrip";
import { convertLunaDate } from "../../../function/convertLunaDate";

interface TripDetailPageProps {}

const TripDetailPage: React.FC<TripDetailPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [trip, setTrip] = useState<ITripDetail | null>(null);
  const [selectedSeats, setSelectedSeats] = useState<ISeatDetail[]>([]);
  const [selectMode, setSelectMode] = useState<"add" | "edit" | null>(null);
  const [targetCustomer, setTargetCustomer] = useState<string | null>(null);
  const [busIdChanged, setBusIdChanged] = useState<string[]>([]);

  const handleGetDetailTrip = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getDetailTripApi(id);
      if (res?.data) {
        setTrip(res?.data);
      } else {
        setTrip(null);
      }
      // Fetch trip detail logic here
    } catch (error) {
      console.error("Error fetching trip detail:", error);
      setTrip(null);
    } finally {
      setLoading(false);
    }
  };
  const handleSeatClick = (seat: ISeatDetail) => {
    // 1️⃣ Nếu chưa có ghế nào được chọn
    if (selectedSeats.length === 0) {
      if (seat.status === "available") {
        // Chọn ghế trống → mode add
        setSelectMode("add");
        setTargetCustomer(null);
        setSelectedSeats([seat]);
      } else if (seat.status === "booked" && seat.customerId) {
        // Chọn ghế đã đặt → mode edit
        setSelectMode("edit");
        setTargetCustomer(seat.customerId);
        setSelectedSeats([seat]);
      } else {
        message.warning("Không thể chọn ghế này");
      }
      return;
    }

    // 2️⃣ Nếu ghế đã tồn tại trong selectedSeats → toggle
    const exists = selectedSeats.some((s) => s._id === seat._id);
    if (exists) {
      const newSeats = selectedSeats.filter((s) => s._id !== seat._id);
      setSelectedSeats(newSeats);

      // Nếu bỏ hết ghế → reset mode và target
      if (newSeats.length === 0) {
        setSelectMode(null);
        setTargetCustomer(null);
      }
      return;
    }

    // 3️⃣ Nếu đang ở mode add → chỉ chọn ghế trống
    if (selectMode === "add") {
      if (seat.status !== "available") {
        message.warning("Chỉ có thể chọn ghế trống khi thêm khách mới");
        return;
      }
      setSelectedSeats([...selectedSeats, seat]);
      return;
    }

    // 4️⃣ Nếu đang ở mode edit → chỉ chọn ghế của cùng khách
    if (selectMode === "edit") {
      if (seat.customerId !== targetCustomer) {
        message.warning("Chỉ được chọn trong ghế của cùng khách");
        return;
      }
      setSelectedSeats([...selectedSeats, seat]);
      return;
    }
  };

  useEffect(() => {
    handleGetDetailTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <>
      {loading ? (
        <LoadingComponent />
      ) : (
        <>
          {trip == null ? (
            <div className="w-full min-h-screen flex justify-center items-center">
              <p>Không tìm thấy chuyến đi !</p>
            </div>
          ) : (
            <div>
              <ActionSeatBar
                selectMode={selectMode}
                selectedSeats={selectedSeats}
                onCancel={() => {
                  setSelectedSeats([]);
                  setSelectMode(null);
                  setTargetCustomer(null);
                }}
                setBusIdChanged={setBusIdChanged}
                onSetTrip={setTrip}
                onConfirm={() => {
                  setSelectedSeats([]);
                  setSelectMode(null);
                  setTargetCustomer(null);
                }}
              />
              <TitleBarComponent
                title={`Vé xe`}
                isBack
                reload={() => {
                  handleGetDetailTrip();
                  setSelectedSeats([]);
                  setSelectMode(null);
                  setTargetCustomer(null);
                }}
                actionButton={
                  <div className="flex items-center gap-2">
                    <ButtonAddBus
                      tripId={trip?._id}
                      onSuccess={handleGetDetailTrip}
                    />
                    <GroupButtonActionTrip
                      trip={trip}
                      onSuccess={handleGetDetailTrip}
                    />
                  </div>
                }
              />
              <div className="w-full p-2.5 rounded-xl bg-white flex flex-col gap-2">
                <div className="w-full flex justify-between gap-2 items-center">
                  <div className="flex flex-col gap-1 w-full">
                    <p className="text-[20px] font-medium ">{trip?.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="rounded-md py-[1px]  px-1.5 bg-gray-400 shadow-md  text-white h-fit w-fit flex justify-center items-center">
                        <p className="text-sm font-medium">
                          {trip?.numberOfDate}N-{trip?.numberOfNight}Đ
                        </p>
                      </div>
                      {!!trip?.isNightDeparture && (
                        <div className="rounded-md py-[1px]  px-1.5 bg-orange-600 shadow-md  text-white h-fit w-fit flex justify-center items-center">
                          <p className="text-sm font-medium">Đi đêm</p>
                        </div>
                      )}
                      {
                        listStatusTrip?.find(
                          (item) => item?.value === trip?.status
                        )?.tag
                      }
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 items-end">
                    <div
                      className={`py-1 px-2 rounded-xl shadow-md bg-red-500`}
                    >
                      <span className="text-white font-bold text-lg">
                        {
                          convertLunaDate(
                            new Date(trip?.departureDate)
                          )?.lunar?.get()?.day
                        }
                        /
                        {
                          convertLunaDate(
                            new Date(trip?.departureDate)
                          )?.lunar?.get()?.month
                        }
                      </span>
                    </div>
                    <span className="text-blue-500 font-medium text-base">
                      {formatLocalDate(trip?.departureDate)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                {trip?.buses.length === 0 && (
                  <div className="flex flex-col items-center gap-2 py-10 rounded-md border-2 border-dashed border-gray-300  ">
                    <p className="text-center text-gray-500">
                      Chưa có xe nào được thêm cho chuyến đi này.
                    </p>
                    <ButtonAddBus
                      tripId={trip?._id}
                      onSuccess={handleGetDetailTrip}
                    />
                  </div>
                )}
                {trip?.buses?.map((bus, index) => (
                  <BusItem
                    isFirstBus={index === 0}
                    trip={trip}
                    key={bus?._id}
                    bus={bus}
                    onSetTrip={setTrip}
                    handleSeatClick={(value) => handleSeatClick(value)}
                    selectMode={selectMode}
                    selectedSeats={selectedSeats}
                    busIdChanged={busIdChanged}
                    setBusIdChanged={setBusIdChanged}
                    targetCustomer={targetCustomer}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TripDetailPage;
